import React, { useMemo, useState } from 'react'
import { Card, Image, ListGroup, Spinner } from 'react-bootstrap'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetMeQuery } from '../../store/user/user'
import { Button } from '../../components/Button/Button'
import { useNavigate, useSearchParams } from 'react-router-dom'
import './Authorize.scss'
import { OAuth2App, OAuth2AuthorizeRequestBody, useGetOAuth2AppByIdQuery, useOauth2AuthorizeMutation } from '../../store/auth/auth'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { isApiErrors } from '../../models/ApiErrors'

interface OAuth2Error {
  error: string
  error_description: string
}

interface ParsedParams {
  clientId: string
  redirectUri: string
  scopes: string[]
  state: string | null
  codeChallengeMethod: string | null
  codeChallenge: string | null
  responseType: string
}

const parseAndValidateParams = (searchParams: URLSearchParams): { params: ParsedParams | null; validationErrors: string[] } => {
  const clientId = searchParams.get('client_id')
  const redirectUri = searchParams.get('redirect_uri')
  const scope = searchParams.get('scope')
  const state = searchParams.get('state')
  const responseType = searchParams.get('response_type')
  const codeChallengeMethod = searchParams.get('code_challenge_method') || null
  const codeChallenge = searchParams.get('code_challenge') || null

  const validationErrors: string[] = []
  if (!clientId) {
    validationErrors.push('client_id is required')
  }
  if (!redirectUri) {
    validationErrors.push('redirect_uri is required')
  }
  if (!scope) {
    validationErrors.push('scope is required')
  }
  if (!responseType) {
    validationErrors.push('response_type is required')
  }
  if ((codeChallengeMethod === null) !== (codeChallenge === null)) {
    validationErrors.push('code_challenge and code_challenge_method must both be provided or both omitted')
  }

  if (validationErrors.length > 0 || !clientId || !redirectUri || !scope || !responseType) {
    return { params: null, validationErrors }
  }

  return {
    params: {
      clientId,
      redirectUri,
      scopes: scope.split(' '),
      state,
      codeChallengeMethod,
      codeChallenge,
      responseType,
    },
    validationErrors: [],
  }
}

const deriveQueryError = (oauth2AppError: unknown, clientId: string | undefined): string | null => {
  if (!oauth2AppError) {
    return null
  }
  const fetchError = oauth2AppError && typeof oauth2AppError === 'object' && 'status' in oauth2AppError ? (oauth2AppError as FetchBaseQueryError) : undefined
  const status = fetchError?.status as number | undefined
  const errorBody = fetchError?.data
  if (status === 404) {
    return `OAuth2 Application with client_id=${clientId} was not found!`
  }
  if (status === 400 && isApiErrors(errorBody) && errorBody.errors.length) {
    return 'OAuth2 integration is not enabled for this account.'
  }
  return 'An unexpected error occurred while fetching the OAuth2 application.'
}

export const OAuthAuthorize: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)

  const [searchParams] = useSearchParams()
  const [runtimeErrors, setRuntimeErrors] = useState<string[]>([])

  const { params, validationErrors } = useMemo(() => parseAndValidateParams(searchParams), [searchParams])
  const {
    isFetching: isFetchingOAuth2App,
    data: oauth2AppData,
    error: oauth2AppError,
    isUninitialized,
  } = useGetOAuth2AppByIdQuery(
    {
      accountId,
      clientId: params!.clientId,
    },
    {
      skip: !params?.clientId,
    },
  )
  const oauth2App = oauth2AppData?.data ?? null
  const queryError = params ? deriveQueryError(oauth2AppError, params.clientId) : null
  const errors = [...validationErrors, ...(queryError ? [queryError] : []), ...runtimeErrors]
  const isLoading = params !== null && (isFetchingOAuth2App || isUninitialized)

  const handleError = (error: OAuth2Error) => {
    setRuntimeErrors([`${error.error}: ${error.error_description}`])
  }

  return (
    <div className='d-flex align-items-center justify-content-center vh-100 bg-light p-3'>
      <Card className='shadow-lg' style={{ width: '22rem', borderRadius: '1rem' }}>
        <Card.Body className='text-center'>
          {isLoading ? (
            <Spinner />
          ) : errors.length || !oauth2App || !params ? (
            <OAuthAuthorizeErrors errors={errors} />
          ) : (
            <OAuthAuthorizeDefault
              clientId={params.clientId}
              redirectUri={params.redirectUri}
              scopes={params.scopes}
              state={params.state}
              codeChallengeMethod={params.codeChallengeMethod}
              codeChallenge={params.codeChallenge}
              oauth2App={oauth2App}
              responseType={params.responseType}
              onError={handleError}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

interface OAuthAuthorizeErrors {
  errors: string[]
}

const OAuthAuthorizeErrors: React.FC<OAuthAuthorizeErrors> = ({ errors }) => {
  return (
    <>
      <Card.Text className='mb-2 text-secondary fw-semibold'>Invalid request:</Card.Text>

      <ListGroup variant='flush' className='mb-3 text-start small'>
        {errors.map((error) => (
          <ListGroup.Item key={error} className='bg-light border-0 ps-3'>
            • {error}
          </ListGroup.Item>
        ))}
      </ListGroup>
    </>
  )
}

interface OAuthAuthorizeDefaultProps {
  clientId: string
  redirectUri: string
  scopes: string[]
  state: string | null
  codeChallengeMethod: string | null
  codeChallenge: string | null
  responseType: string
  oauth2App: OAuth2App
  onError: (error: OAuth2Error) => void
}

const OAuthAuthorizeDefault: React.FC<OAuthAuthorizeDefaultProps> = ({
  clientId,
  redirectUri,
  scopes,
  state,
  codeChallengeMethod,
  codeChallenge,
  responseType,
  oauth2App,
  onError,
}) => {
  const navigate = useNavigate()
  const accountId = useAppSelector(selectAccountId)
  const { data: currentUser, isFetching: isFetchingMe } = useGetMeQuery({ accountId })
  const [triggerOAuth2Authorize, { isLoading: isLoadingAuthorize, isSuccess: isSuccessAuthorize }] = useOauth2AuthorizeMutation()

  const handleAuthorize = async () => {
    const body: OAuth2AuthorizeRequestBody = {
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: responseType,
      scope: scopes.join(' '),
      state,
    }
    if (codeChallengeMethod) {
      body.code_challenge_method = codeChallengeMethod
    }
    if (codeChallenge) {
      body.code_challenge = codeChallenge
    }

    const response = await triggerOAuth2Authorize({ accountId, body })

    if (response.error && 'data' in response.error) {
      onError(response.error.data as OAuth2Error)
      return
    }
    if (response.data) {
      window.location.href = response.data.data.redirectUri
    }
  }

  return (
    <>
      {isFetchingMe || isSuccessAuthorize ? (
        <Spinner />
      ) : (
        <>
          {oauth2App.imageUrl ? (
            <Image src={oauth2App.imageUrl} alt={`${oauth2App.name} logo`} roundedCircle className='mb-3 app-logo' />
          ) : (
            <>
              <i className='bi bi-shield-lock text-primary mb-3' />
            </>
          )}

          <Card.Title className='mb-2'>
            <strong>{oauth2App.name}</strong> is requesting access
          </Card.Title>

          {currentUser && (
            <div className='text-muted mb-3 d-flex align-items-center justify-content-center gap-2'>
              <i className='bi bi-person-circle' />
              <small>
                Signed in as <strong>{currentUser.name}</strong>
              </small>
            </div>
          )}

          <Card.Text className='mb-2 text-secondary fw-semibold'>This app will be able to:</Card.Text>

          <ListGroup variant='flush' className='mb-3 text-start small'>
            {scopes?.map((scope) => (
              <ListGroup.Item key={scope} className='bg-light border-0 ps-3'>
                • {scope}
              </ListGroup.Item>
            ))}
          </ListGroup>

          <div className='d-flex justify-content-between mt-3'>
            <Button
              variant='secondary'
              onClick={() => {
                navigate('/')
              }}
              disabled={false}
              className='w-50 me-2'
            >
              Deny
            </Button>

            <Button variant='primary' onClick={handleAuthorize} disabled={false} className='w-50 ms-2' loading={isLoadingAuthorize}>
              Authorize
            </Button>
          </div>

          <div className='text-muted mt-3 small' style={{ lineHeight: '1.5' }}>
            By clicking Authorize, you allow this app and Zocks to use your information in accordance with their respective Terms of Service and Privacy Policy.
          </div>
        </>
      )}
    </>
  )
}
